import { jsPDF } from 'jspdf';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import CurrencyInput from 'react-currency-input-field';
import toast from 'react-hot-toast';

import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { RegisterSearchFormI } from '../../resources/form-props';
import { RegisterSearchFormValidate } from '../../resources/form-validator';

import { useBankFeedApi } from '../../resources/hooks/api/bankFeedApiHook';
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook';
import { useCreditCardApi } from '../../resources/hooks/api/creditCardApiHook';
import { useGlClassificationApi } from '../../resources/hooks/api/glClassificationApi';
import { useTransTableDataApi } from '../../resources/hooks/api/transApiHook';
import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';

import JournalEntryModal from 'components/Modal/ViewRegister/JournalEntryModal';
import { useAllVendors } from '../../resources/useAllVendors';
import VendorFormModal, {
    EMPTY_VENDOR,
    VendorFormValues,
} from '../Tax1099Vendors/VendorFormModal';
import html2canvas from 'html2canvas';
import { Alert, Dropdown, InputGroup, Spinner } from 'react-bootstrap';
import plusIcon from '../../assets/images/plus-icon-w.svg';
import SAlert from '../../components/SAlert';
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle';
import SelectCustomOption from '../../components/SelectCustomOption';
import { GL_VP, MONTHS, SELECT_CUSTOM_STYLE } from '../../resources/constants';
import { JournalEntryRowI } from '../../resources/form-props';
import { calculateMaxHeightContent, formatTransactionDate, lastYears, usdCurrencyFormat } from '../../resources/functions';
import { getBranch } from '../../store/user/selectors';
import './GlClassification.css';
import { registerDateParams, registerPeriodFileName, registerYears } from './registerDateFilters';

interface Props {

}
interface SearchI {
    year: number,
    month: number,
    type: number
}

type SortField =
    | 'transaction_id'
    | 'transaction_at'
    | 'amount'
    | 'debit'
    | 'credit';

type SortDirection = 'asc' | 'desc';

const HeaderIcon = ({
    children,
    className = '',
}: {
    children: ReactNode
    className?: string
}) => (
    <svg
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden="true"
    >
        {children}
    </svg>
);

const SortIndicatorIcon = ({ direction }: { direction: SortDirection }) =>
    direction === 'asc' ? (
        <HeaderIcon>
            <path
                fillRule="evenodd"
                d="M8 12a.5.5 0 0 0 .5-.5V3.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 3.707V11.5A.5.5 0 0 0 8 12z"
            />
        </HeaderIcon>
    ) : (
        <HeaderIcon>
            <path
                fillRule="evenodd"
                d="M8 4a.5.5 0 0 0-.5.5v7.793L5.354 10.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 12.293V4.5A.5.5 0 0 0 8 4z"
            />
        </HeaderIcon>
    );


const ViewRegister: React.FC<Props> = () => {

    const { glTransactionlist } = useGlClassificationApi();
    const { glAccountsList } = useChartAccountApi();
    const { fetchRegisterTransData, feedUpdate,feedBulkUpdate, exportTransactionsCsv } = useBankFeedApi();
    const { checkYearLock } = useCreditCardApi();
    const { createTransactionAdjustment } = useTransTableDataApi()
    const { createVendor, fetchNextVendorCode } = useTax1099Api();

    const branchData = useSelector(getBranch)
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation("scenes.glClassification.viewRegister");
    const currentDate = moment().format('YYYY-MM-DD');

    const contentRef: any = useRef<HTMLDivElement | null>(null);

    const [records, setRecords] = useState<any>([]);
    const [sorting, setSorting] = useState<Record<SortField, SortDirection>>({
        transaction_id: 'asc',
        transaction_at: 'asc',
        amount: 'asc',
        debit: 'asc',
        credit: 'asc',
    });
    const [data, setData] = useState<any>([]);
    const [calculatedTotalAmount, setCalculatedTotalAmount] = useState<any>({
        totalCredit: 0,
        totalDebit: 0
    });
    const [glCreditAccount, setGlCreditAccount] = useState<any[]>([]);
    const [glAllAccount, setGlAllAccount] = useState<any[]>([]);
    const [glSearchAccount, setGlSearchAccount] = useState<any[]>([]);
    const [glEquityAccount, setGlEquityAccount] = useState<any[]>([]);
    const [glBankTransferAccount, setGlBankTransferAccount] = useState<any[]>([]);
    const [glSalesAccount, setGlSalesAccount] = useState<any[]>([]);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedCalendar, setSelectedCalendar] = useState<any>({
        date_mode: 'month',
        start_date: '',
        end_date: '',
        year: new Date().getFullYear(),
        month: 1,
        amount: null,
        description: '',
        account_id: null
    });
    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#fff'));
    const [showCreateAdjustmentModal, setShowCreateAdjustmentModal] = useState(false);
    const [showQuickVendorModal, setShowQuickVendorModal] = useState(false);
    const [quickVendorInitial, setQuickVendorInitial] = useState<VendorFormValues>(EMPTY_VENDOR);
    const [quickVendorSaving, setQuickVendorSaving] = useState(false);
    const [selectedVendorForModal, setSelectedVendorForModal] = useState<number | null>(null);
    const [savedTransactionId, setSavedTransactionId] = useState<number | null>(null);
    const [duplicateConflict, setDuplicateConflict] = useState<{ params: any[]; branchId: number; conflicts: any[] } | null>(null);
    const [isThirdPartyEnabled, setIsThirdPartyEnabled] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);
    const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set())
    const [batchToAccount, setBatchToAccount] = useState<any>(null)
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const [yearLocked, setYearLocked] = useState<boolean>(false);
    const [yearLockChecking, setYearLockChecking] = useState<boolean>(false);
    const tableContainerRef: any = useRef<HTMLDivElement | null>(null)
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
    // Tracks the Transaction Register list GET independently of isLoading, which is
    // reserved for the Journal Entry save (and other page actions like bulk update).
    // This lets the Journal Entry "saved" state resolve immediately on POST success
    // without waiting for the subsequent register refetch to complete.
    const [isRegisterRefreshing, setIsRegisterRefreshing] = useState<boolean>(false);
    const pageSize = 500;

    // Ref to track latest fetching state inside scroll event listeners without stale closures
    const fetchingRef = useRef(false);
    const observerTargetRef = useRef<HTMLDivElement | null>(null);
    fetchingRef.current = isRegisterRefreshing || isFetchingMore;

    // Add this ref to track the latest selectedCalendar state without stale closures:
    const selectedCalendarRef = useRef(selectedCalendar);
    selectedCalendarRef.current = selectedCalendar;

    useEffect(() => {
        fechAllAccountList().then(r => {
            setGlCreditAccount(r);
        })

        fechAllAccountList('', [...GL_VP.SALES_ACTIVITY.account, ...GL_VP.BANK_ACCOUNT.account]).then(r => {
            setGlAllAccount(r);
            setGlSearchAccount(r)
        })

        fechAllAccountList('', [], GL_VP.EQUITY.account).then(r => {
            setGlEquityAccount(r);
        })

        fechAllAccountList('distribution', GL_VP.EQUITY.account).then((r:any) => {
            setGlBankTransferAccount(r);
        })

        fechAllAccountList('', [], GL_VP.SALES_ACTIVITY.account).then(r => {
            setGlSalesAccount(r);
        })

    }, [])

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(98));
    })

    const { control: control2, handleSubmit: handleSubmit2, reset, setValue, watch, clearErrors, formState: { errors: errors2 } } = useForm<RegisterSearchFormI>({
        defaultValues: {
            date_mode: 'month',
            start_date: '',
            end_date: '',
            month: {
                value: '01',
                label: t('month.01', { ns: 'resources.form-validator' })
            },
            year: { value: '' + new Date().getFullYear(), label: '' + new Date().getFullYear() },
            amount: null,
            description: '',
            account_id: null
        },
        resolver: yupResolver(RegisterSearchFormValidate)
    });

    const dateMode = watch('date_mode');

    const filterAllAccountListExceptBankSales = async (inputValue: string) => {
        const data = await fechAccountList(inputValue)
        return data;
    };

    const handleSelectAll = (checked: boolean) => {
    setSelectedTransactions(
        checked
            ? new Set(data.map((rec: any) => rec.transaction_id))
            : new Set()
    )
}

    const fechAllAccountList = (searchText: string = '', excludeVP: number[] = [], parentAccountIn: number[] = []): Promise<any[]> => {
        return new Promise<any>((resolve1, reject1) => {
            const params: any = {
                "page": 1,
                "per_page": 100,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }

            /*if (excludeVP.length) {
                params['parent_account_not_in'] = excludeVP.join(',')
            }*/
            // params['parent_name_in'] = 'BANK ACCOUNTS';
            // params['parent_account_not_in'] = GL_VP.BANK_ACCOUNT.account.join(',');

            if (parentAccountIn.length) {
                params['parent_account_in'] = parentAccountIn.join(',');
            }

            glAccountsList(params, (message: string, resp: any) => {
                const seen = new Set<string>();
                const data: any[] = [];
                (resp.data.data.result || []).forEach((e: any) => {
                    const label = `${e['account']} ${e['name']}`;  // REV-586: account number first
                    if (!seen.has(label)) {
                        seen.add(label);
                        data.push({
                            label: label,
                            value: e['id']
                        });
                    }
                });
                // setGlAllAccount(data)
                return resolve1(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve1([]);
            })
        })
    }


  const fetchBankFeedList = (pageNum: number = 1, isNewFilter: boolean = false) => {
    // Prevent duplicate triggers
    if (fetchingRef.current && !isNewFilter) return;
    const appliedFilter = selectedCalendarRef.current;
    const params: any = {
        "page": pageNum,
        "per_page": pageSize,
        ...registerDateParams(appliedFilter),
        branch_id: +branchData['id']
    };

    if (selectedCalendarRef.current['amount'] !== null) params['amount'] = selectedCalendarRef.current['amount'];
    if (selectedCalendarRef.current['description']) params['description'] = selectedCalendarRef.current['description'];
    if (selectedCalendarRef.current['account_id']) params['account_id'] = selectedCalendarRef.current['account_id'];
    params['is_updated'] = 1;

    if (pageNum === 1) {
        setIsRegisterRefreshing(true);
    } else {
        setIsFetchingMore(true);
    }

    fetchingRef.current = true;
    fetchRegisterTransData(params, (message: string, resp: any) => {
        if (selectedCalendarRef.current !== appliedFilter) return;
        setIsThirdPartyEnabled(resp.data.data?.is_third_party_enable ?? false);

        const result = resp.data.data?.result || [];
        const totalCount = resp.data.data?.total ?? 0;
        const totalCredit=resp.data.data?.total_credit ?? 0;
        const totalDebit=resp.data.data?.total_debit ?? 0;
        // Process incoming records into component structure
        const storeData: any[] = [];
        result.forEach((rec: any) => {

            const header = rec.breakdowns.map((breakdown: any, index: any) => {
                if (breakdown.account_chart) {
                    if ((rec.account_type == 3) && (breakdown.account_type == 'Deposit')) {
                        return {
                            id: breakdown.id,
                            previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            account_type: breakdown.account_type,
                            name: (breakdown.account_chart && breakdown.account_chart.name) || '',
                            account: (breakdown.account_chart && breakdown.account_chart.account) || '',
                            account_value: breakdown.account_value,
                            debit: breakdown.txn_type == 1 ? breakdown.account_value : 0,
                            credit: breakdown.txn_type == 2 ? breakdown.account_value : 0,
                            revrex_account_id: (breakdown.account_chart && breakdown.account_chart.revrex_account_id) || null,
                            isEdit: false,
                            showLoader: false,
                            errorMsg: null
                        };
                    } else if ((rec.account_type == 1) && (breakdown.account_type == 'Credit_Deposit')) {
                        return {
                            id: breakdown.id,
                            previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            account_type: breakdown.account_type,
                            name: breakdown.account_chart.name,
                            account: breakdown.account_chart.account,
                            account_value: breakdown.txn_type == 2 ? breakdown.account_value * -1 : breakdown.account_value,
                            debit: breakdown.txn_type == 1 ? breakdown.account_value : 0,
                            credit: breakdown.txn_type == 2 ? breakdown.account_value : 0,
                            revrex_account_id: breakdown.account_chart.revrex_account_id,
                            isEdit: false,
                            showLoader: false,
                            errorMsg: null
                        };
                    } else if ((rec.account_type == 2) && (breakdown.account_type == 'Gross_Sale')) {
                        return {
                            id: breakdown.id,
                            previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            account_type: breakdown.account_type,
                            name: breakdown.account_chart.name,
                            account: breakdown.account_chart.account,
                            account_value: breakdown.account_value,
                            debit: breakdown.txn_type == 1 ? breakdown.account_value : 0,
                            credit: breakdown.txn_type == 2 ? breakdown.account_value : 0,
                            revrex_account_id: breakdown.account_chart.revrex_account_id,
                            isEdit: false,
                            showLoader: false,
                            errorMsg: null
                        };
                    } else if ((rec.account_type == 4) && (breakdown.account_type == 'Overshort')) {
                        return {
                            id: breakdown.id,
                            previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            account_type: breakdown.account_type,
                            name: breakdown.account_chart.name,
                            account: breakdown.account_chart.account,
                            account_value: breakdown.account_value,
                            debit: breakdown.txn_type == 1 ? breakdown.account_value : 0,
                            credit: breakdown.txn_type == 2 ? breakdown.account_value : 0,
                            revrex_account_id: breakdown.account_chart.revrex_account_id,
                            isEdit: false,
                            showLoader: false,
                            errorMsg: null
                        };
                    } else if (rec.account_type == 5) {
                        if (index === 0) {
                            const accountValue = breakdown.txn_type == 2 ? breakdown.account_value * -1 : breakdown.account_value;
                            return {
                                id: breakdown.id,
                                previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                    label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                    value: breakdown.account_chart.id
                                }) || null,
                                master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                    label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                    value: breakdown.account_chart.id
                                }) || null,
                                account_type: breakdown.account_type,
                                name: breakdown.account_chart.name,
                                account: breakdown.account_chart.account,
                                account_value: accountValue,
                                debit: breakdown.txn_type == 1 ? breakdown.account_value : 0,
                                credit: breakdown.txn_type == 2 ? breakdown.account_value : 0,
                                revrex_account_id: breakdown.account_chart.revrex_account_id,
                                isEdit: false,
                                showLoader: false,
                                errorMsg: null
                            };
                        }
                        return null;
                    } else if ((rec.account_type == 6) && ['Credit_Deposit', 'Debit_Deposit'].includes(breakdown.account_type)) {
                        return {
                            id: breakdown.id,
                            previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                value: breakdown.account_chart.id
                            }) || null,
                            account_type: breakdown.account_type,
                            name: breakdown.account_chart.name,
                            account: breakdown.account_chart.account,
                            account_value: breakdown.txn_type == 2 ? breakdown.account_value * -1 : breakdown.account_value,
                            debit: breakdown.txn_type == 1 ? breakdown.account_value : 0,
                            credit: breakdown.txn_type == 2 ? breakdown.account_value : 0,
                            revrex_account_id: breakdown.account_chart.revrex_account_id,
                            isEdit: false,
                            showLoader: false,
                            errorMsg: null
                        };
                    }
                    return null;
                }
            }).filter((item: any) => item);

                let isFirstGrossSaleSkipped = false;

                // Only ONE breakdown becomes the header row (header[0] below), so only that
                // one may be withheld from the sub-rows. Excluding its whole account_type
                // instead dropped every sibling of the same type: a split deposit with ten
                // Credit_Deposit lines rendered one and silently discarded nine, from the
                // display AND from totalCredit — which is what produced "debits and credits
                // differ by $1,633.39" on a month whose breakdowns sum to zero difference in
                // the database (3 transactions x 9 hidden lines, Jan 2024).
                const headerBreakdownId = header.length ? header[0].id : null;

                const subObjects = rec.breakdowns
                    .map((breakdown: any, index: any) => {
                        if (breakdown.account_chart) {
                            if (rec.account_type == 5) {
                                if (index === 0) return null;
                                return {
                                    id: breakdown.id,
                                    previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                        label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                        value: breakdown.account_chart.id
                                    }) || null,
                                    master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                        label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                        value: breakdown.account_chart.id
                                    }) || null,
                                    account_type: breakdown.account_type,
                                    name: (breakdown.account_chart && breakdown.account_chart.name) || '',
                                    account: (breakdown.account_chart && breakdown.account_chart.account) || '',
                                    account_value: breakdown.account_value,
                                    debit: breakdown.txn_type == 1 ? breakdown.account_value : "",
                                    credit: breakdown.txn_type == 2 ? breakdown.account_value : "",
                                    revrex_account_id: (breakdown.account_chart && breakdown.account_chart.revrex_account_id) || null,
                                    isEdit: false,
                                    showLoader: false,
                                    errorMsg: null
                                };
                            }
                            if (
                                // Identity, not type: the header row is a specific breakdown.
                                // Credit_Deposit / Deposit / Overshort siblings beyond it are
                                // real lines and belong in the sub-rows and the totals.
                                (breakdown.id !== headerBreakdownId) &&
                                // Gross_Sale has its own branch below; the two Adjustment types
                                // are rendered by the rec.account_type == 5 branch above.
                                (breakdown.account_type !== 'Gross_Sale') &&
                                (breakdown.account_type !== 'Credit_Adjustment') &&
                                (breakdown.account_type !== 'Debit_Adjustment')
                            ) {
                                if (rec.account_type == 6) {

                                } else {
                                    return {
                                        id: breakdown.id,
                                        previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                            label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                            value: breakdown.account_chart.id
                                        }) || null,
                                        master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                            label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                            value: breakdown.account_chart.id
                                        }) || null,
                                        account_type: breakdown.account_type,
                                        name: (breakdown.account_chart && breakdown.account_chart.name) || '',
                                        account: (breakdown.account_chart && breakdown.account_chart.account) || '',
                                        account_value: breakdown.account_value,
                                        debit: breakdown.txn_type == 1 ? breakdown.account_value : "",
                                        credit: breakdown.txn_type == 2 ? breakdown.account_value : "",
                                        revrex_account_id: (breakdown.account_chart && breakdown.account_chart.revrex_account_id) || null,
                                        isEdit: false,
                                        showLoader: false,
                                        errorMsg: null
                                    };
                                }

                            }

                            // Handle 'Gross_Sale' case
                            if (breakdown.account_type === 'Gross_Sale') {
                                if (isFirstGrossSaleSkipped) {
                                    return {
                                        id: breakdown.id,
                                        previous_master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                            label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                            value: breakdown.account_chart.id
                                        }) || null,
                                        master_chart_of_account_id: (breakdown.master_chart_of_account_id && {
                                            label: breakdown.account_chart.name + '(' + breakdown.account_chart.account + ')',
                                            value: breakdown.account_chart.id
                                        }) || null,
                                        account_type: breakdown.account_type,
                                        name: (breakdown.account_chart && breakdown.account_chart.name) || '',
                                        account: (breakdown.account_chart && breakdown.account_chart.account) || '',
                                        account_value: breakdown.account_value,
                                        debit: breakdown.txn_type == 1 ? breakdown.account_value : "",
                                        credit: breakdown.txn_type == 2 ? breakdown.account_value : "",
                                        revrex_account_id: (breakdown.account_chart && breakdown.account_chart.revrex_account_id) || null,
                                        isEdit: false,
                                        showLoader: false,
                                        errorMsg: null
                                    };
                                } else {
                                    isFirstGrossSaleSkipped = true;
                                    return null; // Skip the first row with 'Gross_Sale'
                                }
                            }
                        }
                    return null;
                })
                .filter((item: any) => item);

            storeData.push({
                parent_id: rec.parent_id,
                transaction_id: rec.id,
                description: rec.description,
                vendor_id: rec.vendor_id ?? null,
                transaction_at: rec.transaction_at,
                header: (header.length && header[0]) || {},
                subRecord: subObjects,
            });
        });

        // Append to existing records if pageNum > 1, otherwise set fresh
        setData((prev: any[]) => {
            const updated = pageNum === 1 ? storeData : [...prev, ...storeData];

            // Re-calculate totals over accumulated data
            const calculatedTotal = updated.reduce((acc: any, record: any) => {
                const _total = record['subRecord'].reduce((subAcc: any, _record: any) => {
                    subAcc['totalSubDebit'] += Math.abs(_record['debit'] || 0);
                    subAcc['totalSubCredit'] += Math.abs(_record['credit'] || 0);
                    return subAcc;
                }, { totalSubDebit: 0, totalSubCredit: 0 });
                acc['totalDebit'] += Math.abs((record['header'] && record['header']['debit']) || 0) + _total['totalSubDebit'];
                acc['totalCredit'] += Math.abs((record['header'] && record['header']['credit']) || 0) + _total['totalSubCredit'];
                return acc;
            }, { totalDebit: 0, totalCredit: 0 });

            setCalculatedTotalAmount({
                totalCredit:totalCredit|| calculatedTotal['totalCredit'],
                totalDebit: totalDebit || calculatedTotal['totalDebit']
            });

            // Determine whether more data can be fetched
            setHasMore((updated.length < totalCount) && (result.length === pageSize));
            return updated;
        });

        fetchingRef.current = false;
        setIsRegisterRefreshing(false);
        setIsFetchingMore(false);
    }, (message: string, resp: any) => {
        if (selectedCalendarRef.current !== appliedFilter) return;
        fetchingRef.current = false;
        setIsRegisterRefreshing(false);
        setIsFetchingMore(false);
        toast.error(message);
    });
};

useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchBankFeedList(1, true);
}, [selectedCalendar]);

// Handle table scroll event for infinite pagination
useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const handleScroll = () => {
        if (!hasMore || fetchingRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = tableContainer;
        // Trigger next fetch when user is 150px away from the bottom of table container
        if ((scrollHeight - scrollTop - clientHeight) <= 150) {
            setCurrentPage((prevPage) => {
                const nextPage = prevPage + 1;
                fetchBankFeedList(nextPage);
                return nextPage;
            });
        }
    };

    tableContainer.addEventListener('scroll', handleScroll);
    return () => tableContainer.removeEventListener('scroll', handleScroll);
}, [hasMore]);
useEffect(() => {
        const target = observerTargetRef.current;
        if (!target) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                // When target enters the viewport root margin and we aren't currently fetching
                if (first.isIntersecting && hasMore && !fetchingRef.current) {
                    setCurrentPage((prevPage) => {
                        const nextPage = prevPage + 1;
                        fetchBankFeedList(nextPage);
                        return nextPage;
                    });
                }
            },
            {
                root: tableContainerRef.current, // Limits observation to the scrollable container
                rootMargin: '0px 0px 400px 0px', // 💡 Pre-fetches 400px BEFORE the target enters view
                threshold: 0.1,
            }
        );

        observer.observe(target);

        return () => observer.disconnect();
 }, [hasMore]);

const filterAllAccountList = async (inputValue: string) => {
        const data = await fechAllAccountList(inputValue)
        return data;
    };

    const filterEquityAccountList = async (inputValue: string) => {
        return fechAllAccountList(inputValue, [], GL_VP.EQUITY.account);
    };
    const filterBankTransferAccountList = async (inputValue: string) => {
        const filtered = await fechAllAccountList(inputValue, GL_VP.EQUITY.account);
        const distributionFiltered = filtered.filter((item: any) =>
            item?.label?.toLowerCase().includes('distribution')
        );
        return distributionFiltered;
    };
    const filterSalesAccountList = async (inputValue: string) => {
        return fechAllAccountList(inputValue, [], GL_VP.SALES_ACTIVITY.account);
    };

    const getAccountOptions = (accountType: string) => {
        switch (accountType) {
            case 'Equity':
                return { defaultOptions: glEquityAccount, loadOptions: filterEquityAccountList };
            case 'Bank_Transfer':
            case 'Deposit':
            case 'Credit_Deposit':
                return { defaultOptions: glBankTransferAccount, loadOptions: filterBankTransferAccountList };
            case 'Gross_Sale':
                return { defaultOptions: glSalesAccount, loadOptions: filterSalesAccountList };
            default:
                return { defaultOptions: glCreditAccount, loadOptions: filterAllAccountList };
        }
    };

    const filterSearchAccountList = async (inputValue: string) => {
        const data = await fechAllAccountList(inputValue)
        return data;
    };

    /*const resetFilter = () => {
        reset({
            month: {
                value: 0,
                label: 'All'
            },
            year: { value: 0, label: 'All' },
            amount: null
        })
        setSelectedCalendar({
            year: 0,
            month: 0,
            amount: null
        })
    }*/

    const resetFilter = () => {
        setValue('date_mode', 'month');
        setValue('start_date', '');
        setValue('end_date', '');
        clearErrors();
        setValue('amount', null);
        setValue('description', '');
        setValue('account_id', null);
        setSelectedCalendar({
            ...selectedCalendar,
            date_mode: 'month',
            start_date: '',
            end_date: '',
            month: Number(watch('month').value),
            year: Number(watch('year').value),
            amount: null,
            description: '',
            account_id: null
        })
        setSelectedTransactions(new Set())
        setBatchToAccount(null)
    }

    const processSearchData = (data: RegisterSearchFormI) => {
        setSelectedTransactions(new Set());
        setBatchToAccount(null);
        setSelectedCalendar({
            date_mode: data.date_mode,
            start_date: data.start_date,
            end_date: data.end_date,
            year: +data['year']['value'],
            month: +data['month']['value'],
            amount: data['amount'],
            description: data['description'],
            account_id: data['account_id']?.value || null
        })
    }

    function groupBy(arr: any, property: any) {
        return arr.reduce((acc: any, obj: any) => {
            const key = obj[property];
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(obj);
            return acc;
        }, {});
    }

    const onChangeMasterChart = (event: any, groupIndex: number, recordIndex: number | null) => {
        setData((prevState: any) => {
            const objects = JSON.parse(JSON.stringify(prevState));
            const groupData = objects[groupIndex];
            if (recordIndex === null) {
                groupData['header']['master_chart_of_account_id'] = event;
            } else {
                groupData['subRecord'][recordIndex]['master_chart_of_account_id'] = event;
            }
            objects.splice(groupIndex, 1, groupData)

            return objects;
        })
    }

    const _handleEdit = (groupIndex: number, recordIndex: number | null, toggle: boolean) => {
        setData((prevState: any) => {
            const objects = JSON.parse(JSON.stringify(prevState));
            const groupData = objects[groupIndex];

            if (recordIndex === null) {
                if (!toggle) {
                    groupData['header']['master_chart_of_account_id'] = groupData['header']['previous_master_chart_of_account_id'];
                }
                groupData['header']['isEdit'] = toggle;
                console.log('groupData: ', groupData)
            } else {
                if (!toggle) {
                    groupData['subRecord'][recordIndex]['master_chart_of_account_id'] = groupData['subRecord'][recordIndex]['previous_master_chart_of_account_id'];
                }
                groupData['subRecord'][recordIndex]['isEdit'] = toggle;
            }
            objects.splice(groupIndex, 1, groupData)

            return objects;
        })
    }

    const _handleLoader = (groupIndex: number, recordIndex: number | null, toggle: boolean) => {
        setData((prevState: any) => {
            const objects = JSON.parse(JSON.stringify(prevState));
            const groupData = objects[groupIndex];
            if (recordIndex === null) {
                groupData['header']['showLoader'] = toggle;
            } else {
                groupData['subRecord'][recordIndex]['showLoader'] = toggle;
            }
            objects.splice(groupIndex, 1, groupData)

            return objects;
        })
    }

    const _handleUpdate = (record: any, groupIndex: number, recordIndex: number | null) => {
        _handleLoader(groupIndex, recordIndex, true);
        const objects = JSON.parse(JSON.stringify(data));
        const groupData = objects[groupIndex];

        if (!record['master_chart_of_account_id']) {
            if (recordIndex === null) {
                groupData['header']['errorMsg'] = 'GL is required';
            } else {
                groupData['subRecord'][recordIndex]['errorMsg'] = 'GL is required';
            }
            objects.splice(groupIndex, 1, groupData)
            setData(objects);
            return;
        }

        processData(record).then((resp: any) => {
            if (recordIndex === null) {
                groupData['header']['previous_master_chart_of_account_id'] = record['master_chart_of_account_id'];
                groupData['header']['isEdit'] = false;
            } else {
                groupData['subRecord'][recordIndex]['previous_master_chart_of_account_id'] = record['master_chart_of_account_id'];
                groupData['subRecord'][recordIndex]['isEdit'] = false;
            }
            objects.splice(groupIndex, 1, groupData)
            setData(objects);
            _handleLoader(groupIndex, recordIndex, false);
        }).catch((err: any) => {
            _handleLoader(groupIndex, recordIndex, false);
        })

    }

    const processData = (record: any) => {
        return new Promise<any>((resolve, reject) => {
            const params: any = {
                breakdown_id: record['id'],
                master_chart_of_account_id: record['master_chart_of_account_id']['value'],
                branch_id: +branchData['id']
            }
            feedUpdate(params, (message: string, resp: any) => {
                toast.success(message);
                return resolve(resp)
            }, (message: string, resp: any) => {
                toast.error(message);
                return reject(resp);
            })
        })
    }
    const toTwoDecimal = (value: any) => parseFloat(value).toFixed(2);

    const yearLockRequest = useRef(0);
    const checkRegisterYearLock = async (onUnlocked?: () => void, showLockedToast: boolean = true) => {
        const request = ++yearLockRequest.current;
        setYearLockChecking(true);
        const results = await Promise.all(registerYears(selectedCalendar).map(year => new Promise<number | null>(resolve => {
            checkYearLock(
                { branch_id: +branchData['id'], year, section: 2 },
                (_msg: string, resp: any) => resolve(resp?.data?.data?.locked === true ? year : null),
                // Backend validation remains authoritative if the advisory check is unavailable.
                () => resolve(null),
            );
        })));
        if (request !== yearLockRequest.current) return;
        const lockedYears = results.filter(year => year !== null);
        setYearLockChecking(false);
        setYearLocked(lockedYears.length > 0);
        if (lockedYears.length > 0) {
            setIsLoading(false);
            if (showLockedToast) toast.error(t('filter.date.locked_years', { years: lockedYears.join(', ') }));
            return;
        }
        onUnlocked?.();
    };

    const handleAdjustmentClick = () => {
        checkRegisterYearLock(() => setShowCreateAdjustmentModal(true));
    };

    useEffect(() => {
        checkRegisterYearLock(undefined, false);
    }, [selectedCalendar.year, selectedCalendar.date_mode, selectedCalendar.start_date, selectedCalendar.end_date, branchData['id']]);


    const fechAccountList = (searchText: string = '', excludeVP: number[] = [], includeVP: number[] = []): Promise<any[]> => {
        return new Promise<any>((resolve, reject) => {
            const params: any = {
                "page": 1,
                "per_page": 100,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }
            if (excludeVP.length) {
                params['parent_account_not_in'] = excludeVP.join(',')
            }
            if (includeVP.length) {
                params['parent_account_in'] = includeVP.join(',')
            }

            glAccountsList(params, (message: string, resp: any) => {
                const seen = new Set<string>();
                const data: any[] = [];
                (resp.data.data.result || []).forEach((e: any) => {
                    const label = `${e['account']} ${e['name']}`;  // REV-586: account number first
                    if (!seen.has(label)) {
                        seen.add(label);
                        data.push({
                            label: label,
                            value: e['id']
                        });
                    }
                });
                return resolve(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        })
    }

    // Every vendor on the branch, through the shared cache the 1099 screens
    // use. The bespoke loader this replaces was empty in three independent
    // ways: it read the first callback argument as the response when that
    // argument is the message string, it looked for `items` where the endpoint
    // returns `results`, and it sent `per_page` where the parameter is
    // `page_size` (capped server-side at 200, so 500 was never honoured
    // either). createVendor invalidates the cache, so a quick-add lands here
    // without a manual refetch.
    const { vendors } = useAllVendors({
        branchId: branchData?.id ? Number(branchData.id) : null,
    });
    const vendorNameById = useMemo(
        () => new Map(vendors.map((v: any) => [Number(v.id), `${v.display_name || ''}`.trim()])),
        [vendors],
    );
    // Display only: the stored description is untouched, so search and export
    // still see the bank's own text.
    const registerDescription = (rec: any) => {
        const parts = [rec.description, vendorNameById.get(Number(rec.vendor_id))].filter(Boolean);
        return parts.join(' | ') || t('message.na');
    };

    const handleOpenQuickAddVendor = (queryName: string) => {
        const cleanName = (queryName || '').trim();
        let maxNum = 0;
        vendors.forEach((v) => {
            if (v.vendor_code && v.vendor_code.startsWith('VND-')) {
                const num = parseInt(v.vendor_code.slice(4), 10);
                if (!isNaN(num) && (num > maxNum)) maxNum = num;
            }
        });
        const fallbackCode = `VND-${String(maxNum + 1).padStart(5, '0')}`;

        setQuickVendorInitial({
            ...EMPTY_VENDOR,
            display_name: cleanName,
            vendor_code: fallbackCode,
            default_form_type: '1099-NEC',
            default_box: 'NEC-1',
            is_1099_eligible: '1',
            source: 'manual',
        });
        setShowQuickVendorModal(true);

        const branchId = branchData ? +branchData['id'] : null;
        if (branchId) {
            fetchNextVendorCode(
                { branch_id: branchId },
                (response: any) => {
                    const nextCode = response?.data?.data?.vendor_code;
                    if (nextCode) {
                        setQuickVendorInitial((prev: any) => ({ ...prev, vendor_code: nextCode }));
                    }
                }
            );
        }
    };

    const handleQuickVendorSubmit = (values: VendorFormValues) => {
        const branchId = branchData ? +branchData['id'] : null;
        if (!branchId) return;
        setQuickVendorSaving(true);
        const payload: Record<string, any> = {
            ...values,
            branch_id: branchId,
        };
        createVendor(
            payload,
            (message: string, response: any) => {
                setQuickVendorSaving(false);
                setShowQuickVendorModal(false);
                const newVendor = response?.data?.data ?? {};
                toast.success(message || 'Vendor created successfully');
                if (newVendor.id) {
                    setSelectedVendorForModal(newVendor.id);
                }
            },
            (message: string) => {
                setQuickVendorSaving(false);
                toast.error(message || 'Failed to save vendor');
            }
        );
    };

    // REV-586 — updated to accept JournalEntryRowI[] from the new double-entry modal
    const submitAdjustment = (
        rows: JournalEntryRowI[],
        headerDescription: string,
        transactionAt: Date,
        vendorId?: number | null
    ) => {
        // Map each journal entry row to a breakdown record
        const breakdowns: any[] = rows
            .filter((row) => ((row.debit_amount || 0) > 0) || ((row.credit_amount || 0) > 0))
            .map((row) => {
                const isDebit = (row.debit_amount || 0) > 0;
                return {
                    description: row.notes || headerDescription,
                    amount: isDebit ? row.debit_amount : row.credit_amount,
                    master_chart_of_account_id: row.master_chart_of_account_id?.value || 0,
                    txn_type: isDebit ? 1 : 2,       // 1 = Debit, 2 = Credit
                    account_type: isDebit ? 'Debit_Adjustment' : 'Credit_Adjustment',
                };
            });

        if (breakdowns.length === 0) {
            setIsLoading(false);
            toast.error(t('action.adjustment.empty_entry'));
            return;
        }

        const totalDebit  = rows.reduce((s, r) => s + (r.debit_amount  || 0), 0);
        const totalCredit = rows.reduce((s, r) => s + (r.credit_amount || 0), 0);

        // Debits first in the register display
        const debitBreakdowns  = breakdowns.filter((b) => b.txn_type === 1);
        const creditBreakdowns = breakdowns.filter((b) => b.txn_type === 2);
        const sortedBreakdowns = [...debitBreakdowns, ...creditBreakdowns];

        const firstDebit  = debitBreakdowns[0];
        const firstCredit = creditBreakdowns[0];

        const params: any[] = [{
            description:                          headerDescription,
            transaction_at:                       moment(transactionAt).format('YYYY-MM-DDTHH:mm:ss'),
            adjustment_debit:                     +totalDebit.toFixed(2),
            adjustment_credit:                    +totalCredit.toFixed(2),
            adjustment_master_debit_account_id:   firstDebit?.master_chart_of_account_id  || 0,
            adjustment_master_credit_account_id:  firstCredit?.master_chart_of_account_id || 0,
            breakdowns:                           sortedBreakdowns,
            vendor_id:                            vendorId || null,
        }];

        postAdjustment(params, +branchData['id']);
    };

    // Posts the adjustment and routes a 409 duplicate-conflict response to the
    // confirmation dialog. Callable a second time with `keep`/`overwrite` set on
    // each item once the user has chosen.
    const postAdjustment = (params: any[], branchId: number) => {
        setIsLoading(true);
        checkRegisterYearLock(() => {
            createTransactionAdjustment({ params, branch_id: branchId }, (message: string, resp: any) => {
                // The Journal Entry save itself is complete as soon as the POST succeeds —
                // stop the saving loader immediately and show the success panel right away.
                // The register refetch is deliberately deferred to the modal's onHide (see
                // the JournalEntryModal usage below), so the success popup is never covered
                // by a loading state while the user is looking at it. onHide triggers the
                // refetch via isRegisterRefreshing whenever savedTransactionId is non-null.
                setIsLoading(false);
                toast.success(message);
                const txnId: number | null = resp?.data?.data?.transaction_id ?? null;
                setSavedTransactionId(txnId);
            }, (message: string, resp: any) => {
                // Check for year_locked error from backend validation
                if (resp && resp.error && (resp.error.type === 'year_locked')) {
                    setIsLoading(false);
                    const lockedYears = resp.error.locked_years || [];
                    toast.error(`Year(s) ${lockedYears.join(', ')} is/are locked. Transaction Adjustment is not allowed for locked years.`);
                    return;
                }

                // Server signals a duplicate conflict — let the user choose between
                // keeping both, overwriting, or cancelling. resp is the parsed
                // `data` from the error response (see appApiCallHook).
                if (resp && resp.error && (resp.error.type === 'duplicate_adjustment')) {
                    // Clear the page-level spinner while the modal dialog is up,
                    // otherwise it overlays the dialog body and looks like a hang.
                    // It gets turned back on inside resolveDuplicateConflict when
                    // the user chooses Keep/Overwrite and we resubmit.
                    setIsLoading(false);
                    setDuplicateConflict({ params, branchId, conflicts: resp.error.conflicts || [] });
                    return;
                }

            // Possibly-partial save: either a true network drop, or a server
            // 5xx that came AFTER the inserts already committed. create_adjustments
            // does heavy post-insert work (bank-rec writes, log_reports + Celery
            // dispatch, log_cashflow_report, update_bs_pnl_calculations) — any of
            // which can fail or time out AFTER the rows are in the DB. In that
            // case the user sees an error toast but their adjustment is actually
            // saved; if they retry they hit our duplicate dialog and get
            // confused. Refetch the register so they can see the real state
            // and decide whether a retry is needed.
            const isNetworkError = !resp && /network\s*error/i.test(message);
            const isServerError = !!resp && (typeof (resp.status_code || resp.status) === 'number')
                && ((resp.status_code || resp.status) >= 500);
            if (isNetworkError || isServerError) {
                toast.error(
                    isNetworkError
                        ? t('action.network_error')
                        : t('action.server_error', { message })
                );
                // The adjustment may already be committed server-side (see comment above) —
                // stop the Journal Entry saving loader now rather than leaving it tied to
                // the register refetch, then close the modal and let the register refresh
                // independently via isRegisterRefreshing.
                setIsLoading(false);
                setShowCreateAdjustmentModal(false);
                fetchBankFeedList(1, true);
                return;
            }

                setIsLoading(false);
                toast.error(message);
            })
        }, true);
    };

    const resolveDuplicateConflict = (mode: 'keep' | 'overwrite') => {
        if (!duplicateConflict) return;
        const flag = mode === 'keep' ? { keep: true } : { overwrite: true };
        const updatedParams = duplicateConflict.params.map((p: any) => ({ ...p, ...flag }));
        const branchId = duplicateConflict.branchId;
        setDuplicateConflict(null);
        // Turn the page-level spinner back on for the resubmit — it was cleared
        // when we opened the duplicate dialog so it wouldn't overlay the modal.
        setIsLoading(true);
        // On retry after duplicate conflict, do not re-show the lock toast
        // since user already saw it on initial submission.
        postAdjustment(updatedParams, branchId);
    };

    const cancelDuplicateConflict = () => {
        setDuplicateConflict(null);
        setIsLoading(false);
    };

    const getSortValue = (record: any, field: SortField) => {
        switch (field) {
            case 'transaction_id':
                return Number(record?.transaction_id || 0);
            case 'transaction_at':
                return moment(record?.transaction_at).valueOf();
            case 'amount':
                return Number(record?.header?.account_value || 0);
            case 'debit':
                return Number(record?.header?.debit || 0);
            case 'credit':
                return Number(record?.header?.credit || 0);
            default:
                return 0;
        }
    };

    const sortTable = (field: SortField, action: number) => {
        setData((prevData: any[]) => {
            const sortedData = [...prevData].sort((a, b) => {
                const firstValue = getSortValue(a, field);
                const secondValue = getSortValue(b, field);

                if (firstValue === secondValue) {
                    return 0;
                }

                return action === 1
                    ? firstValue > secondValue ? 1 : -1
                    : firstValue < secondValue ? 1 : -1;
            });

            return sortedData;
        });

        setSorting((prevSorting) => ({
            ...prevSorting,
            [field]: action === 1 ? 'asc' : 'desc',
        }));
    };

    useEffect(() => {
        const year: string = searchParams.get('year') || ('' + new Date().getFullYear());
        const month: string = searchParams.get('month') || ('' + 1);

        setValue('year', {
            value: year,
            label: year
        });
        setValue('month', {
            value: month,
            label: MONTHS[+month - 1].label
        });

        setSelectedCalendar({
            ...selectedCalendar,
            year: +year,
            month: +month,
        })
    }, [searchParams])

    function chunkArray<T>(arr: T[], size: number): T[][] {
        const result: T[][] = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    }

    const _handlePdfGenerator = async () => {
        if (!contentRef.current) {
            return;
        }

        try {
            setIsDownloading(true)
            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 15;
            const contentWidth = pageWidth - (margin * 2);
            const pdf = new jsPDF('p', 'mm', 'a4');

            // Cloning the table to avoid modifications to the original DOM
            const originalTable = contentRef.current.querySelector('.table');
            const clonedTable = originalTable.cloneNode(true) as HTMLElement;

            // Adding unique class for PDF export
            clonedTable.classList.add('pdf-export-table');

            // Adding PDF-only classes to thead/tbody
            const thead = clonedTable.querySelector('thead');
            if (thead) thead.classList.add('pdf-export-header');
            const tbodies = clonedTable.querySelectorAll('tbody');
            tbodies.forEach(tbody => tbody.classList.add('pdf-export-row'));

            // Removing action column (the 9th column in each row)
            const actionCells = clonedTable.querySelectorAll('th:nth-child(9), td:nth-child(9)');
            actionCells.forEach(cell => cell.remove());

            // Preparing the temp div
            const tempDiv = document.createElement('div');
            tempDiv.style.position = 'absolute';
            tempDiv.style.left = '-9999px';

            // Injecting custom styles for PDF only, scoped to .pdf-export-table
            const style = document.createElement('style');
            style.innerHTML = `
                .pdf-export-table .pdf-export-header th {
                    background: #1560C5 !important;
                    color: #fff !important;
                    font-weight: bold !important;
                    border: 1px solid #2064bdff !important;
                    padding: 8px !important;
                }
                .pdf-export-table .pdf-export-row th:nth-child(3),
                .pdf-export-table .pdf-export-row td:nth-child(3) {
                    white-space: normal !important;
                    word-break: break-word !important;
                    max-width: 300px !important;
                    overflow-wrap: break-word !important;
                }
                .pdf-export-table .pdf-export-row .text-truncate {
                    white-space: normal !important;
                    overflow: visible !important;
                    text-overflow: unset !important;
                    max-width: 300px !important;
                }
                .pdf-export-table .pdf-export-header th:nth-child(1),
                .pdf-export-table .pdf-export-row th:nth-child(1),
                .pdf-export-table .pdf-export-row td:nth-child(1) {
                    min-width: 90px !important;
                    width: 90px !important;
                }
            `;
            tempDiv.appendChild(style);
            tempDiv.appendChild(clonedTable);
            document.body.appendChild(tempDiv);

            // Now selecting from tempDiv, not from the main DOM
            const tableHeaderSection = tempDiv.querySelector('.pdf-export-header');
            const tableBodySection = tempDiv.querySelectorAll('.pdf-export-row');

            const addCustomHeader = (doc: jsPDF, pageNum: number) => {
                doc.setFontSize(10);
                doc.setTextColor(40);
                const monthName = MONTHS[+selectedCalendar.month - 1]?.label || '';
                doc.text(selectedCalendar.date_mode === 'range'
                    ? t("table.summary.report.range", { start: selectedCalendar.start_date, end: selectedCalendar.end_date })
                    : t("table.summary.report.title", { monthName, year: selectedCalendar.year }), margin, 8);

                doc.setFontSize(8);
                doc.text(t("table.summary.report.generated", { date: moment().format('YYYY-MM-DD HH:mm:ss') }), pageWidth - 70, 8);

                doc.setDrawColor(200);
                doc.line(margin, 10, pageWidth - margin, 10);
            };

            addCustomHeader(pdf, 1);

            let currentY = 14;

            if (tableHeaderSection) {
                const contentCanvas = await html2canvas(tableHeaderSection as HTMLElement, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    allowTaint: true
                });

                const contentImgWidth = contentWidth;
                const contentImgHeight = (contentCanvas.height * contentWidth) / contentCanvas.width;

                if (contentImgHeight > (pageHeight - currentY - margin)) {
                    pdf.addPage();
                    currentY = margin;
                }

                pdf.addImage(contentCanvas.toDataURL('image/png'), 'PNG', margin, currentY, contentImgWidth, contentImgHeight);

                currentY += contentImgHeight;
            }

            for (let i = 0; i < tableBodySection.length; i++) {
                const row = tableBodySection[i] as HTMLElement;
                const rowCanvas = await html2canvas(row, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    allowTaint: true
                });

                const rowImgWidth = contentWidth;
                const rowImgHeight = (rowCanvas.height * contentWidth) / rowCanvas.width;

                if (rowImgHeight > (pageHeight - currentY - margin)) {
                    pdf.addPage();
                    currentY = margin;
                }

                pdf.addImage(rowCanvas.toDataURL('image/png'), 'PNG', margin, currentY, rowImgWidth, rowImgHeight);

                currentY += rowImgHeight;
            }

            // Cleaning up the temporary element
            document.body.removeChild(tempDiv);

            const pdfOutput = pdf.output('blob');
            const pdfUrl = URL.createObjectURL(pdfOutput);
            const a = document.createElement('a');
            a.href = pdfUrl;
            a.download = `transaction_register_${registerPeriodFileName(selectedCalendar)}.pdf`;
            a.style.display = 'none';

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(pdfUrl);
            setIsDownloading(false);

        } catch (error) {
            console.error('Error generating PDF:', error);
            setIsDownloading(false);
        }
    }

    const _handleCsvExport = () => {
        if (data.length === 0) {
            toast.error(t('action.export.no_data'));
            return;
        }

        setIsExportingCsv(true);

        // Using the same parameters as fetchRegisterTransData
        const params: any = {
            "page": 1,
            "per_page": 500,
            ...registerDateParams(selectedCalendar),
            branch_id: +branchData['id']
        }

        if (selectedCalendar['amount'] !== null) {
            params['amount'] = selectedCalendar['amount'];
        }
        if (selectedCalendar['description']) {
            params['description'] = selectedCalendar['description'];
        }
        if (selectedCalendar['account_id']) {
            params['account_id'] = selectedCalendar['account_id'];
        }
        // params['id'] = 0;
        params['is_updated'] = 1;

        exportTransactionsCsv(params, (msg: string, blob: any) => {
            try {

                if (!(blob instanceof Blob)) {
                    toast.error(t('action.export.csv.invalid_format'));
                    setIsExportingCsv(false);
                    return;
                }

                const url = window.URL.createObjectURL(blob);

                // Create download link
                const link = document.createElement('a');
                link.href = url;
                link.download = `transaction_register_${registerPeriodFileName(selectedCalendar)}.csv`;
                link.style.display = 'none';

                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up
                window.URL.revokeObjectURL(url);

                toast.success(t('action.export.csv.success'));
            } catch (error) {
                toast.error(t('action.export.csv.failed'));
            }
            setIsExportingCsv(false);
        },
            (message: string, resp: any) => {
                setIsExportingCsv(false);
                toast.error(message || t('action.export.failed'));
            }
        );
    }

    const totalDebit = calculatedTotalAmount["totalDebit"].toFixed(2);
    const totalCredit = calculatedTotalAmount["totalCredit"].toFixed(2);
    const isBalanced = totalDebit === totalCredit;
    const balanceDiff = Math.abs(calculatedTotalAmount["totalDebit"] - calculatedTotalAmount["totalCredit"]).toFixed(2);
    const registerMonth = selectedCalendar.month;
    const registerYear = selectedCalendar.year;
    const getNextSortAction = (field: SortField) => sorting[field] === 'asc' ? -1 : 1;

    return (
        <div className="main-container flex-grow-1" ref={contentRef}>
            <div className="container-fluid">
               <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2">
                        <div >
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">{t("title")}</h1>
                        </div>
                    <div className="filter-btn">
                    <button
                        type="button"
                        onClick={() => setShowFilter(!showFilter)}
                        className="button-primary d-flex align-items-center"
                        style={{ width: 'max-content' }}
                    >
                        <i
                        className={`fa ${showFilter ? 'fa-times' : 'fa-filter'} me-2`}
                        aria-hidden="true"
                        ></i>
                        {showFilter ? t('filter.btn.hide') : t('filter.btn.show')}
                    </button>
                </div>
                </div>

                <div className=" executive-dashboard p-2">
                    <div className="dashboard-top-part">
                        {showFilter&&(
                        <div className="table-top pdf-container" id="ux_page_filter">
                            <div className="row">
                                <Form onSubmit={handleSubmit2(processSearchData)}>
                                    <div className="btn-group mb-3" role="group" aria-label={t('filter.date.mode')}>
                                        {(['month', 'range'] as const).map(mode => (
                                            <button key={mode} type="button" aria-pressed={dateMode === mode}
                                                className={dateMode === mode ? 'btn btn-primary' : 'btn btn-outline-primary'}
                                                onClick={() => { setValue('date_mode', mode); clearErrors(); }}>
                                                {t(mode === 'month' ? 'filter.date.month_view' : 'filter.date.custom_range')}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="col-sm-12">
                                        <div className="filter-wrap d-flex justify-content-between align-items-center">
                                            <div className="d-flex flex-grow-1 flex-wrap">
                                                {dateMode === 'month' ? <>
                                                <Form.Group className="filter-box" controlId="formBasicEmail">
                                                    <Controller
                                                        name="month"
                                                        control={control2}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={MONTHS}
                                                                className={'zindex-10'}
                                                            />
                                                        )}
                                                    />

                                                    {
                                                        (errors2 && errors2['month'] && errors2['month'].message && (
                                                            <Form.Text className="text-danger">
                                                                {errors2['month'].message as ReactNode || ''}
                                                            </Form.Text>
                                                        )) || ''
                                                    }

                                                </Form.Group>
                                                <Form.Group className="filter-box" controlId="formBasicEmail">
                                                    <Controller
                                                        name="year"
                                                        control={control2}
                                                        render={({ field }) => (
                                                            // <Select
                                                            //     {...field}
                                                            //     options={[{label: 'All', value: 0}, ...lastYears(5)]}
                                                            //     className={'zindex-10'}
                                                            // />
                                                            <Select
                                                                {...field}
                                                                options={lastYears(5)}
                                                                className={'zindex-10'}
                                                            />
                                                        )}
                                                    />
                                                    {
                                                        (errors2 && errors2['year'] && errors2['year'].message && (
                                                            <Form.Text className="text-danger">
                                                                {errors2['year'].message as ReactNode || ''}
                                                            </Form.Text>
                                                        )) || ''
                                                    }

                                                </Form.Group>
                                                </> : <>
                                                    {(['start_date', 'end_date'] as const).map(name => (
                                                        <Form.Group key={name} className="filter-box" controlId={name}>
                                                            <Controller name={name} control={control2} render={({ field }) => (
                                                                <Form.Control {...field} type="date" min="0001-01-01" max={currentDate}
                                                                    placeholder={t('filter.date.' + name)}
                                                                    aria-label={t('filter.date.' + name)}
                                                                    isInvalid={!!errors2[name]} />
                                                            )} />
                                                            <Form.Control.Feedback type="invalid" className="d-block">
                                                                {errors2[name]?.message}
                                                            </Form.Control.Feedback>
                                                        </Form.Group>
                                                    ))}
                                                </>}
                                                <Form.Group className="filter-box w-10" controlId="formBasicEmail">
                                                    <Controller
                                                        name={`amount`}
                                                        control={control2}
                                                        render={({ field }) => (
                                                            <InputGroup hasValidation>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <CurrencyInput
                                                                    className={`form-control`}
                                                                    placeholder="0.00"
                                                                    value={field.value ?? ''}
                                                                    onValueChange={(value) => {
                                                                        field.onChange(value || null);
                                                                    }}
                                                                    allowDecimals={true}
                                                                    decimalsLimit={2}
                                                                    decimalSeparator="."
                                                                    groupSeparator=","
                                                                    disableAbbreviations
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleSubmit2(processSearchData)();
                                                                        }
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                        )}
                                                    />
                                                    {
                                                        (errors2 && errors2['amount'] && errors2['amount'].message && (
                                                            <Form.Text className="text-danger">
                                                                {errors2['amount'].message as ReactNode || ''}
                                                            </Form.Text>
                                                        )) || ''
                                                    }

                                                </Form.Group>

                                                <Form.Group className="filter-box" controlId="formBasicEmail">
                                                    <Controller
                                                        name={`description`}
                                                        control={control2}
                                                        render={({ field }) => (
                                                            <InputGroup hasValidation>
                                                                <input
                                                                    type={"text"}
                                                                    className={`form-control`}
                                                                    placeholder={t('filter.placeholder.description')}
                                                                    {...field}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleSubmit2(processSearchData)();
                                                                        }
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                        )}
                                                    />
                                                    {
                                                        (errors2 && errors2['description'] && errors2['description'].message && (
                                                            <Form.Text className="text-danger">
                                                                {errors2['description'].message as ReactNode || ''}
                                                            </Form.Text>
                                                        )) || ''
                                                    }

                                                </Form.Group>

                                                <Form.Group className="filter-box" controlId="formBasicEmail">
                                                    <Controller
                                                        name={`account_id`}
                                                        control={control2}
                                                        render={({ field: { value, onChange, onBlur } }) => (
                                                            <AsyncSelect
                                                                isClearable={true}
                                                                placeholder={t('filter.placeholder.select')}
                                                                defaultOptions={glSearchAccount}
                                                                loadOptions={filterSearchAccountList}
                                                                onChange={(e: any) => {
                                                                    onChange(e);
                                                                }}
                                                                onKeyDown={(e: any) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        handleSubmit2(processSearchData)();
                                                                    }
                                                                }}
                                                                className="vp-dropdown zindex-10"
                                                                menuPosition="fixed"
                                                                value={value}
                                                                defaultValue={value}
                                                                styles={selectCustomStyle}
                                                            />
                                                        )}
                                                    />
                                                    {
                                                        (errors2 && errors2['account_id'] && errors2['account_id'].message && (
                                                            <Form.Text className="text-danger">
                                                                {errors2['account_id'].message as ReactNode || ''}
                                                            </Form.Text>
                                                        )) || ''
                                                    }

                                                </Form.Group>

                                                <div className="filter-btn">
                                                    <button type="button" onClick={handleSubmit2(processSearchData)}><i className="fa fa-search pr-5px" aria-hidden="true"></i> {t('finter.btn.title')}</button>
                                                </div> &nbsp;&nbsp;
                                                <div className="filter-btn">
                                                    <button type="button" className='button-secondary' onClick={resetFilter}><i className='fa fa-refresh' /> &nbsp;&nbsp;{t('filter.reset.btn.title')}</button>
                                                </div>&nbsp;&nbsp;

                                                {
                                                    <div>
                                                        <Dropdown align="end">
                                                            <Dropdown.Toggle
                                                                variant="secondary"
                                                                disabled={isDownloading || isExportingCsv || (data.length === 0)}
                                                                className="button-secondary d-flex align-items-center export-dropdown-toggle"
                                                            >
                                                                {(isDownloading || isExportingCsv) ? (
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true">
                                                                    </span>) : (
                                                                    <i className="fa fa-download me-2" />
                                                                )}

                                                                {t('action.export.title')}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item
                                                                    onClick={_handleCsvExport}
                                                                    className="d-flex align-items-center custom-selected"
                                                                >
                                                                    <span>{t('action.export.csv.btn.title')}</span>
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    onClick={_handlePdfGenerator}
                                                                    className="d-flex align-items-center custom-selected"
                                                                >
                                                                    <span>{t('action.export.pdf.btn.title')}</span>
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                }
                                                {/* Transaction Adjustment button — hoisted into the top filter
                                                    row so it stays visible as the user scrolls the register
                                                    table. Same gating as before (hidden when third-party is
                                                    enabled or no rows have been loaded yet). */}
                                                {!isThirdPartyEnabled && (data.length > 0) && (
                                                    <>
                                                        &nbsp;&nbsp;
                                                        <div className="filter-btn">
                                                            <button
                                                                type="button"
                                                                onClick={handleAdjustmentClick}
                                                                disabled={yearLocked || yearLockChecking}
                                                                title={yearLocked ? t('filter.date.locked_period') : undefined}
                                                                className="button-tertiary d-flex align-items-center"
                                                                style={{ width: 'max-content' }}
                                                            >
                                                                <img className="mx-1" src={plusIcon} alt="" /> {t('transaction.adjustment')}
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="filter-btn">
                                        <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                            <div className="loader"></div>
                                        </div>
                                    </div>
                                </Form>
                            </div>
                        </div>
                        )}
                        {/* Lightweight, non-blocking indicator for the register refetch that follows a
                            Journal Entry save (or a background register refresh). Deliberately kept
                            separate from the dark full-screen .page-loader overlay, which remains tied
                            to isLoading (Journal Entry saving / bulk update) only. */}
                        {isRegisterRefreshing && (
                            <div className="register-refresh-indicator d-inline-flex align-items-center gap-2 mb-2 px-3 py-2 rounded shadow-sm">
                                <Spinner animation="border" size="sm" variant="primary" />
                                <div>
                                    <div className="register-refresh-indicator__title">
                                        {t('register.refreshing.title')}
                                    </div>
                                    <div className="register-refresh-indicator__subtitle">
                                        {t('register.refreshing.subtitle')}
                                    </div>
                                </div>
                            </div>
                        )}
                           <div
              className="table-responsive list-table table-scroll"
              ref={tableContainerRef}
              style={{ maxHeight: !showFilter ? 'calc(100vh - 180px)' : 'calc(100vh - 230px)', overflowY: 'auto' }}
            >
                            {!isBalanced && (
                                <Alert variant={'info'}>
                                    <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                                    {t('table.unbalanced_alert', {
                                        monthYear: selectedCalendar.date_mode === 'range'
                                            ? `${selectedCalendar.start_date} – ${selectedCalendar.end_date}`
                                            : new Date(registerYear, registerMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' }),
                                        amount: (+balanceDiff).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    })}
                                </Alert>
                                  )}

                            {(selectedTransactions.size > 0) && (
    <div className="d-flex align-items-center gap-3 mb-2">
        <span>{selectedTransactions.size} {t('table.selected_count')}</span>
        <div style={{ width: '250px' }}>
            <AsyncSelect
               placeholder={t('table.batch.placeholder')}
                defaultOptions={glCreditAccount}
                loadOptions={filterAllAccountList}
                menuPosition="fixed"
                styles={selectCustomStyle}
                menuPortalTarget={document.body}
                onChange={(e: any) => setBatchToAccount(e)}
                value={batchToAccount}
            />
        </div>

        <div className="filter-btn">
            <button type="button" disabled={isLoading || !batchToAccount} onClick={() => {
    const breakdown_ids = data
        .filter((rec: any) => selectedTransactions.has(rec.transaction_id))
        .flatMap((rec: any) => {
            const headerAccountTypes = ['Gross_Sale', 'Credit_Adjustment', 'Debit_Adjustment']
            if (headerAccountTypes.includes(rec.header?.account_type)) {
                if (rec.header?.master_chart_of_account_id?.value === selectedCalendar.account_id) {
                    return [rec.header.id]
                }
            }
            return rec.subRecord
                .filter((sub: any) => sub.master_chart_of_account_id?.value === selectedCalendar.account_id)
                .map((sub: any) => sub.id)
        })
        setIsLoading(true)
    feedBulkUpdate({
        breakdown_ids,
        master_chart_of_account_id: batchToAccount.value,
        branch_id: +branchData['id']
    }, (message: string, resp: any) => {
        setIsLoading(false)
       toast.success(t('action.bulk.success'))
        setValue('account_id', batchToAccount)
        setSelectedCalendar({
        ...selectedCalendar,
        account_id: batchToAccount.value
    })
        setSelectedTransactions(new Set())
        setBatchToAccount(null)
        fetchBankFeedList()
    }, (message: string, resp: any) => {
        setIsLoading(false)
        toast.error(message)
    })
}}>
   {t('table.batch.apply_btn')}
</button>
        </div>

    </div>
)}

                            {/* Transaction Adjustment button moved into the top filter row
                                (next to Search / Reset / Export) so it stays visible while
                                scrolling the register table. */}
                            <table className="table table-default">
                                <thead className="pdf-table-header">
                                    <tr className="align-middle">
                                        <th style={{ width: '40px' }}>
                                            {selectedCalendar.account_id && !isThirdPartyEnabled &&
                                            (<input
                                                type="checkbox"
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                checked={(selectedTransactions.size > 0) && (selectedTransactions.size === data.length)}
                                            />)}
                                        </th>
                                        <th className="text-start">
                                            <button
                                                type="button"
                                                className="align-items-center bg-transparent border-0 d-inline-flex gap-1 p-0 text-white"
                                                onClick={() => sortTable('transaction_id', getNextSortAction('transaction_id'))}
                                            >
                                                <span>{t('table.header.txn.id')}</span>
                                                <SortIndicatorIcon direction={sorting.transaction_id} />
                                            </button>
                                        </th>
                                        <th className="text-center">
                                            <button
                                                type="button"
                                                className="align-items-center bg-transparent border-0 d-inline-flex gap-1 justify-content-center p-0 text-white"
                                                onClick={() => sortTable('transaction_at', getNextSortAction('transaction_at'))}
                                            >
                                                <span>{t('table.header.txn.date')}</span>
                                                <SortIndicatorIcon direction={sorting.transaction_at} />
                                            </button>
                                        </th>
                                        <th className="text-start">{t('table.header.description')}</th>
                                        <th className="text-end">
                                            <button
                                                type="button"
                                                className="align-items-center bg-transparent border-0 d-inline-flex gap-1 justify-content-end p-0 text-white w-100"
                                                onClick={() => sortTable('amount', getNextSortAction('amount'))}
                                            >
                                                <span>{t('table.header.amount')}</span>
                                                <SortIndicatorIcon direction={sorting.amount} />
                                            </button>
                                        </th>
                                        <th className="text-end"></th>
                                        <th className="text-start">{t('table.header.account')}</th>
                                        <th className="text-end">
                                            <button
                                                type="button"
                                                className="align-items-center bg-transparent border-0 d-inline-flex gap-1 justify-content-end p-0 text-white w-100"
                                                onClick={() => sortTable('debit', getNextSortAction('debit'))}
                                            >
                                                <span>{t('table.header.debit')}</span>
                                                <SortIndicatorIcon direction={sorting.debit} />
                                            </button>
                                        </th>
                                        <th className="text-end">
                                            <button
                                                type="button"
                                                className="align-items-center bg-transparent border-0 d-inline-flex gap-1 justify-content-end p-0 text-white w-100"
                                                onClick={() => sortTable('credit', getNextSortAction('credit'))}
                                            >
                                                <span>{t('table.header.credit')}</span>
                                                <SortIndicatorIcon direction={sorting.credit} />
                                            </button>
                                        </th>
                                        <th className="text-center">{t('table.header.action')}</th>
                                    </tr>
                                </thead>
                                {/* <tbody> */}
                                {
                                    data.length > 0 ? (
                                        <>
                                            {chunkArray(data, 4).map((group, groupIndex) => (
                                                <tbody key={groupIndex} className="pdf-table-row">
                                                    {group.map((rec: any, index: any) => (
                                                        <React.Fragment key={index}>
                                                            <tr
                                                                className={
                                                                    (index % 2) === 0
                                                                        ? "align-middle"
                                                                        : "align-middle grey-background"
                                                                }
                                                            >
                                                            <td>
                                                                {selectedCalendar.account_id && !isThirdPartyEnabled && (
                                                                 <input
                                                                     type="checkbox"
                                                                     checked={selectedTransactions.has(rec.transaction_id)}
                                                                     onChange={() => {
                                                                         setSelectedTransactions(prev => {
                                                                          const next = new Set(prev)
                next.has(rec.transaction_id)
                    ? next.delete(rec.transaction_id)
                    : next.add(rec.transaction_id)
                return next
            })
        }}
    />)}
</td>
                                                                <th className="text-start">{rec.transaction_id}</th>
                                                                <th className="text-center">
                                                                    {formatTransactionDate(rec.transaction_at)}
                                                                </th>
                                                                <th
                                                                    className="text-start text-truncate"
                                                                    style={{ maxWidth: "110px" }}
                                                                    title={registerDescription(rec)}
                                                                >
                                                                    {registerDescription(rec)}
                                                                </th>
                                                                <th className="text-end">
                                                                    {rec?.header?.account_value < 0
                                                                        ? `(${usdCurrencyFormat(
                                                                            Math.abs(rec?.header?.account_value)
                                                                        )})`
                                                                        : usdCurrencyFormat(rec?.header?.account_value || 0)}
                                                                </th>
                                                                <th></th>
                                                                <th className="text-start">
                                                                    {rec.header.isEdit ? (
                                                                        <>
                                                                            <AsyncSelect
                                                                                isClearable={false}
                                                                                placeholder={t("table.acc.select.placeholder")}
                                                                                defaultOptions={getAccountOptions(rec.header.account_type).defaultOptions}
                                                                                loadOptions={getAccountOptions(rec.header.account_type).loadOptions}
                                                                                defaultValue={rec.header.master_chart_of_account_id}
                                                                                onChange={(e: any) =>
                                                                                    onChangeMasterChart(e, (groupIndex * 4) + index, null)
                                                                                }
                                                                                className="vp-dropdown"
                                                                                menuPosition="fixed"
                                                                                styles={selectCustomStyle}
                                                                                components={{ Option: SelectCustomOption }}
                                                                            />
                                                                            {rec.header.errorMsg && (
                                                                                <span className="text-danger">
                                                                                    {rec.header.errorMsg}
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    ) : rec.header.master_chart_of_account_id ? (
                                                                        <span className="text-wrap">
                                                                            {rec.header.master_chart_of_account_id.label}
                                                                        </span>
                                                                    ) : (
                                                                        <span>NA</span>
                                                                    )}
                                                                </th>
                                                                <td className="text-end">
                                                                    {usdCurrencyFormat(Math.abs(rec?.header?.debit || 0))}
                                                                </td>
                                                                <td className="text-end">
                                                                    {usdCurrencyFormat(Math.abs(rec?.header?.credit || 0))}
                                                                </td>
                                                                {[
                                                                    "Gross_Sale",
                                                                    "Credit_Adjustment",
                                                                    "Debit_Adjustment",
                                                                ].includes(rec.header.account_type) &&
                                                                    !isThirdPartyEnabled ? (
                                                                    <th className="text-center">
                                                                        {rec.header.isEdit ? (
                                                                            <div>
                                                                                {rec.header.showLoader ? (
                                                                                    <Spinner animation="border" size="sm" />
                                                                                ) : (
                                                                                    <>
                                                                                        <span
                                                                                            title={t('table.edit.cancel.btn.title')}
                                                                                            className="cursor-pointer text-danger"
                                                                                            onClick={() => _handleEdit((groupIndex * 4) + index, null, false)}
                                                                                        >
                                                                                            <i
                                                                                                className="fa fa-cancel"
                                                                                                aria-hidden="true"
                                                                                            ></i>
                                                                                        </span>
                                                                                        &nbsp;&nbsp;&nbsp;
                                                                                        <span
                                                                                            title={t('table.edit.save.btn.title')}
                                                                                            className="cursor-pointer text-success"
                                                                                            onClick={() => _handleUpdate(rec.header, (groupIndex * 4) + index, null)}
                                                                                        >
                                                                                            <i
                                                                                                className="fa fa-save"
                                                                                                aria-hidden="true"
                                                                                            ></i>
                                                                                        </span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span
                                                                                title={t('table.edit.update.btn.title')}
                                                                                className="cursor-pointer text-secondary"
                                                                                onClick={() => _handleEdit((groupIndex * 4) + index, null, true)}
                                                                            >
                                                                                <i
                                                                                    className="fa fa-pencil"
                                                                                    aria-hidden="true"
                                                                                ></i>
                                                                            </span>
                                                                        )}
                                                                    </th>
                                                                ) : (
                                                                    <th></th>
                                                                )}
                                                            </tr>

                                                            {rec.subRecord.map((record: any, recordIndex: any) => (
                                                                <tr
                                                                    key={recordIndex}
                                                                    className={
                                                                        (index % 2) === 0
                                                                            ? "align-middle"
                                                                            : "align-middle grey-background"
                                                                    }
                                                                >
                                                                    <td></td>
                                                                    <td className="text-start"></td>
                                                                    <td className="text-center"></td>
                                                                    <td className="text-start">
                                                                        <span className="text-truncate d-inline-block" style={{ maxWidth: "150px" }} title={record.description}>
                                                                            {record.description}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-end"></td>
                                                                    <td></td>
                                                                    <td className="text-start">
                                                                        {record.isEdit ? (
                                                                            <>
                                                                                <AsyncSelect
                                                                                    isClearable={false}
                                                                                    placeholder={t("table.acc.select.placeholder")}
                                                                                    defaultOptions={getAccountOptions(record.account_type).defaultOptions}
                                                                                    loadOptions={getAccountOptions(record.account_type).loadOptions}
                                                                                    defaultValue={record.master_chart_of_account_id}
                                                                                    onChange={(e: any) =>
                                                                                        onChangeMasterChart(e, (groupIndex * 4) + index, recordIndex)
                                                                                    }
                                                                                    className="vp-dropdown"
                                                                                    menuPosition="fixed"
                                                                                    styles={selectCustomStyle}
                                                                                    components={{ Option: SelectCustomOption }}
                                                                                />
                                                                                {record.errorMsg && (
                                                                                    <span className="text-danger">
                                                                                        {record.errorMsg}
                                                                                    </span>
                                                                                )}
                                                                            </>
                                                                        ) : record.master_chart_of_account_id ? (
                                                                            <span className="text-wrap">
                                                                                {record.master_chart_of_account_id.label}
                                                                            </span>
                                                                        ) : (
                                                                            <span>NA</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {usdCurrencyFormat(Math.abs(record.debit))}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {usdCurrencyFormat(Math.abs(record.credit))}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {!isThirdPartyEnabled &&
                                                                            (record.isEdit ? (
                                                                                <div>
                                                                                    {record.showLoader ? (
                                                                                        <Spinner animation="border" size="sm" />
                                                                                    ) : (
                                                                                        <>
                                                                                            <span
                                                                                                title={t('table.edit.cancel.btn.title')}
                                                                                                className="cursor-pointer text-danger"
                                                                                                onClick={() =>
                                                                                                    _handleEdit((groupIndex * 4) + index, recordIndex, false)
                                                                                                }
                                                                                            >
                                                                                                <i
                                                                                                    className="fa fa-cancel"
                                                                                                    aria-hidden="true"
                                                                                                ></i>
                                                                                            </span>
                                                                                            &nbsp;&nbsp;&nbsp;
                                                                                            <span
                                                                                                title={t('table.edit.save.btn.title')}
                                                                                                className="cursor-pointer text-success"
                                                                                                onClick={() =>
                                                                                                    _handleUpdate(record, (groupIndex * 4) + index, recordIndex)
                                                                                                }
                                                                                            >
                                                                                                <i
                                                                                                    className="fa fa-save"
                                                                                                    aria-hidden="true"
                                                                                                ></i>
                                                                                            </span>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <span
                                                                                    title={t('table.edit.update.btn.title')}
                                                                                    className="cursor-pointer text-secondary"
                                                                                    onClick={() => _handleEdit((groupIndex * 4) + index, recordIndex, true)}
                                                                                >
                                                                                    <i
                                                                                        className="fa fa-pencil"
                                                                                        aria-hidden="true"
                                                                                    ></i>
                                                                                </span>
                                                                            ))}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            ))}

                                   {(data.length > 0) && (
    <tfoot className="position-sticky bottom-0 bg-white">
      <tr>
        <td colSpan={4} className="text-end fw-bold">
          <strong>{t("table.summary.total")}</strong>
        </td>
        <td></td>
        <td></td>
        <td></td>
        <td className="text-end">
          <h6 className="mb-0 fw-bold">{usdCurrencyFormat(totalDebit)}</h6>
        </td>
        <td className="text-end">
          <h6 className="mb-0 fw-bold">{usdCurrencyFormat(totalCredit)}</h6>
        </td>
        <td></td>
      </tr>
    </tfoot>
  )}
                                        </>
                                    ) : (
                                        <tbody className="pdf-table-row">
                                            <tr>
                                                <td colSpan={10} className="text-center">
                                                    {t("table.summary.no.data")}
                                                </td>
                                            </tr>
                                        </tbody>
                                    )
                                }
                                {/* </tbody> */}
                            </table>
                        {/* Invisible trigger target for pre-fetching */}
                        <div ref={observerTargetRef} style={{ height: '10px', width: '100%' }} />

                        {isFetchingMore && (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" variant="primary" />
                                <span className="ms-2 text-muted small">{t('loading')}</span>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>
            <ScrollToggle scrollRef={tableContainerRef} />
            {/* REV-586 — replaced CreateTransactionAdjustmentModal with JournalEntryModal */}
            {
                showCreateAdjustmentModal ?
                    <JournalEntryModal
                        show={showCreateAdjustmentModal}
                        glAccounts={glAllAccount}
                        onHide={() => {
                            setShowCreateAdjustmentModal(false);
                            // Only refetch if this close follows a successful save (i.e. the user
                            // dismissed the success panel — Close button, header X, or Escape).
                            // Closing while still editing/reviewing (savedTransactionId is null)
                            // must not trigger an unnecessary register refresh.
                            if (savedTransactionId !== null) {
                                fetchBankFeedList(1, true);
                            }
                            setSavedTransactionId(null);
                            setSelectedVendorForModal(null);
                        }}
                        onSave={submitAdjustment}
                        filterAllAccountList={filterAllAccountListExceptBankSales}
                        selectedCalendar={selectedCalendar.date_mode === 'range' ? {
                            ...selectedCalendar,
                            year: Number(selectedCalendar.start_date.slice(0, 4)),
                            month: Number(selectedCalendar.start_date.slice(5, 7)),
                            day: Number(selectedCalendar.start_date.slice(8, 10)),
                        } : selectedCalendar}
                        isSaving={isLoading}
                        savedTransactionId={savedTransactionId}
                        vendors={vendors}
                        onOpenQuickAddVendor={handleOpenQuickAddVendor}
                        initialVendorId={selectedVendorForModal}
                    /> : <></>
            }

            <VendorFormModal
                show={showQuickVendorModal}
                saving={quickVendorSaving}
                initial={quickVendorInitial}
                onClose={() => setShowQuickVendorModal(false)}
                onSubmit={handleQuickVendorSubmit}
            />

            <SAlert
                show={duplicateConflict !== null}
                icon="question"
                 title={t('duplicate.title')}
                msg={duplicateConflict
                    ? (() => {
                        // SAlert's `html` prop maps to react-sweetalert2's html which
                        // takes a plain HTML string, NOT a JSX element. Passing JSX
                        // resulted in "[object Object]" rendering and an unresponsive
                        // dialog. Build a plain text message instead — Swal renders
                        // it via the `text` prop (newlines preserved).
                        const lines = duplicateConflict.conflicts.map((c: any) =>
                            `• ${c.description} on ${moment.utc(c.transaction_at).format('MM/DD/YYYY')} for ${usdCurrencyFormat(c.amount)}`
                        );
                        return [
                            t('duplicate.intro'),
                            '',
                            ...lines,
                            '',
                            t('duplicate.prompt'),
                        ].join('\n');
                    })()
                    : ''
                }
                confirmBtnText={t('duplicate.confirm_btn')}
                denyButtonText={t('duplicate.deny_btn')}
                cancelBtnText={t('duplicate.cancel_btn')}
                showCancel={true}
                showDenyButton={true}
                onConfirm={() => resolveDuplicateConflict('keep')}
                onDeny={() => resolveDuplicateConflict('overwrite')}
                onCancel={cancelDuplicateConflict}
            />

        </div>

    )
}

export default ViewRegister;
